import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { blocksApi } from '@wtf/shared/api/blocks'
import {
    BlockUserResponse,
    UnblockUserResponse,
    GetBlockedUsersResponse,
    BlockStatusResponse,
} from '@wtf/shared'

/**
 * Hook to block a user
 */
export function useBlockUser() {
    const queryClient = useQueryClient()

    return useMutation<
        BlockUserResponse,
        Error,
        { userId: string; reason?: string }
    >({
        mutationFn: ({ userId, reason }) =>
            blocksApi.blockUser(userId, reason ? { reason } : undefined),
        onSuccess: (_data, variables) => {
            // Invalidate block status for this user
            queryClient.invalidateQueries({
                queryKey: ['blockStatus', variables.userId],
            })
            // Invalidate blocked users list
            queryClient.invalidateQueries({ queryKey: ['blockedUsers'] })
            // Invalidate feed-related queries to remove blocked user's content
            queryClient.invalidateQueries({ queryKey: ['feed'] })
            queryClient.invalidateQueries({ queryKey: ['discover'] })
            queryClient.invalidateQueries({ queryKey: ['stories'] })
        },
    })
}

/**
 * Hook to unblock a user
 */
export function useUnblockUser() {
    const queryClient = useQueryClient()

    return useMutation<UnblockUserResponse, Error, string>({
        mutationFn: (userId) => blocksApi.unblockUser(userId),
        onSuccess: (_data, userId) => {
            // Invalidate block status for this user
            queryClient.invalidateQueries({ queryKey: ['blockStatus', userId] })
            // Invalidate blocked users list
            queryClient.invalidateQueries({ queryKey: ['blockedUsers'] })
            // Invalidate feed-related queries
            queryClient.invalidateQueries({ queryKey: ['feed'] })
            queryClient.invalidateQueries({ queryKey: ['discover'] })
            queryClient.invalidateQueries({ queryKey: ['stories'] })
        },
    })
}

/**
 * Hook to get list of blocked users
 */
export function useBlockedUsers() {
    return useQuery<GetBlockedUsersResponse, Error>({
        queryKey: ['blockedUsers'],
        queryFn: () => blocksApi.getBlockedUsers(),
        staleTime: 5 * 60 * 1000, // 5 minutes
    })
}

/**
 * Hook to check if current user has blocked a specific user
 */
export function useBlockStatus(userId: string | undefined) {
    return useQuery<BlockStatusResponse, Error>({
        queryKey: ['blockStatus', userId],
        queryFn: () => blocksApi.getBlockStatus(userId!),
        enabled: !!userId,
        staleTime: 5 * 60 * 1000, // 5 minutes
    })
}
